// Section: Taxability Rules (gated by partsFinancialEngine)
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import type { TaxabilityCategories } from '@/lib/parts-financial/calculator';

const ROWS: { key: keyof TaxabilityCategories; label: string; hint: string }[] = [
  { key: 'parts', label: 'Parts', hint: 'Replacement parts on the ticket' },
  { key: 'labor', label: 'Labor', hint: 'Technician labor charges' },
  { key: 'tires', label: 'Tires', hint: 'Tire products' },
  { key: 'shop_supplies', label: 'Shop Supplies', hint: 'Misc shop supply fees' },
  { key: 'disposal', label: 'Disposal Fees', hint: 'Tire / fluid disposal' },
  { key: 'hazmat', label: 'Hazmat / Environmental', hint: 'Hazmat & env fees' },
  { key: 'core', label: 'Core Charges', hint: 'Refundable core deposits' },
  { key: 'sublet', label: 'Sublet', hint: 'Outsourced work' },
];

interface Props {
  value: TaxabilityCategories;
  onChange: (next: TaxabilityCategories) => void;
}

export function TaxabilityRulesSection({ value, onChange }: Props) {
  const enabled = useFeatureFlag('partsFinancialEngine');
  if (!enabled) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Taxability Rules</CardTitle>
        <CardDescription>Which categories are subject to sales tax in this jurisdiction.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ROWS.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
              <div>
                <Label className="font-medium">{row.label}</Label>
                <p className="text-xs text-muted-foreground">{row.hint}</p>
              </div>
              <Switch
                checked={!!value[row.key]}
                onCheckedChange={(checked) => onChange({ ...value, [row.key]: checked })}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
